/* eslint-disable no-param-reassign */
// TO DO - We should not be reassigning target, need to implement a better solution
import html2canvas from 'html2canvas';
import fileDownload from 'js-file-download';
import api from 'services/apiService';

import { toastNotify } from 'common/ToastNotification/ToastNotification';

import { dateFormats, formatDate } from './dateHelper';

export const EXPORT_FEED_LIMIT = 10000;

/**
 * @function downloadData
 * @param {string} data - The data to download
 * @param {string} name - The name of the file to download
 * @return {void}
 */
export const downloadData = (data, name) => {
  // Add UTF-8 BOM to ensure proper encoding for special characters
  const utf8BOM = '\uFEFF';
  const utf8Data = utf8BOM + data;

  fileDownload(
    utf8Data,
    `${name?.replaceAll(' ', '_')}_${formatDate(new Date(), dateFormats.api)}.csv`,
  );
};

/**
 * @function convertChartDataToCSV
 * @param {Object} chartData - Chart data object with labels and datasets
 * @param {Array} rawData - Optional raw data with actual values (for charts showing percentages)
 * @returns {string} CSV formatted string
 */
export const convertChartDataToCSV = (chartData, rawData) => {
  if (!chartData || !chartData.labels || !chartData.datasets) {
    return '';
  }

  // Check if this is a simple chart (doughnut/pie) with one dataset
  const isSingleDataset = chartData.datasets.length === 1;

  if (isSingleDataset && chartData.labels.length === chartData.datasets[0].data.length) {
    // Check if we have raw data with actual values (for percentage charts)
    if (rawData && Array.isArray(rawData) && rawData.length > 0) {
      // Format for doughnut/pie charts with both actual values and percentages
      const headers = ['Item', 'Value', 'Percentage'];
      const rows = chartData.labels.map((label, index) => {
        // Find the raw data item that matches this label
        const rawItem = rawData.find(item => item.name === label);
        const actualValue = rawItem?.rawValue ?? rawItem?.value ?? '';
        const percentage = chartData.datasets[0].data[index] ?? '';

        // Clean NaN values
        const cleanActualValue = Number.isNaN(actualValue) ? '' : actualValue;
        const cleanPercentage = Number.isNaN(percentage) ? '' : percentage;

        return [label, cleanActualValue, cleanPercentage];
      });

      // Convert to CSV format
      const csvContent = [
        headers.join(','),
        ...rows.map(row =>
          row
            .map(cell => {
              // Escape values that contain commas or quotes
              const cellStr = String(cell);
              if (cellStr.includes(',') || cellStr.includes('"') || cellStr.includes('\n')) {
                return `"${cellStr.replace(/"/g, '""')}"`;
              }
              return cellStr;
            })
            .join(','),
        ),
      ].join('\n');

      return csvContent;
    }

    // Format for doughnut/pie charts without raw data: Label, Value
    const headers = ['Item', 'Value'];
    const rows = chartData.labels.map((label, index) => {
      const value = chartData.datasets[0].data[index] ?? '';
      const cleanValue = Number.isNaN(value) ? '' : value;
      return [label, cleanValue];
    });

    // Convert to CSV format
    const csvContent = [
      headers.join(','),
      ...rows.map(row =>
        row
          .map(cell => {
            // Escape values that contain commas or quotes
            const cellStr = String(cell);
            if (cellStr.includes(',') || cellStr.includes('"') || cellStr.includes('\n')) {
              return `"${cellStr.replace(/"/g, '""')}"`;
            }
            return cellStr;
          })
          .join(','),
      ),
    ].join('\n');

    return csvContent;
  }

  // Format for multi-series charts (line/bar): Period as rows, series as columns
  const headers = ['Period', ...chartData.datasets.map(ds => ds.label || 'Unknown')];
  const rows = [];

  // Build rows - each label (period) gets a row
  chartData.labels.forEach((label, index) => {
    const row = [label];
    chartData.datasets.forEach(dataset => {
      const value = dataset.data?.[index];
      // Handle NaN, null, undefined - convert to empty string
      const cleanValue = value === null || value === undefined || Number.isNaN(value) ? '' : value;
      row.push(cleanValue);
    });
    rows.push(row);
  });

  // Convert to CSV format
  const csvContent = [
    headers.join(','),
    ...rows.map(row =>
      row
        .map(cell => {
          // Escape values that contain commas or quotes
          const cellStr = String(cell);
          if (cellStr.includes(',') || cellStr.includes('"') || cellStr.includes('\n')) {
            return `"${cellStr.replace(/"/g, '""')}"`;
          }
          return cellStr;
        })
        .join(','),
    ),
  ].join('\n');

  return csvContent;
};

/**
 * @function exportDataFn
 * @param {string} name - The name of the data to be exported.
 * @param {function} exportFn - The function used to fetch the data to be exported.
 * @returns {void}
 */
export function exportDataFn(name, exportFn) {
  return exportFn({
    onSuccess: data => downloadData(data, name),
    exportRequest: true,
    headers: { Accept: 'text/csv' },
  });
}

/**
 * @function exportDataWithFilter
 * @param {HTMLElement} scrollableEl - The scrollable element to stretch.
 */
const stretchToContent = scrollableEl => {
  if (scrollableEl) {
    scrollableEl.style.maxHeight = 'none';
    scrollableEl.style.maxWidth = 'none';
    scrollableEl.style.overflow = 'visible';
    scrollableEl.style.height = 'auto';
  }
};

/**
 * @function convertToCanvas
 * @param {HTMLElement} elementToExport - the HTML element to convert to a canvas
 * @returns {Promise<HTMLCanvasElement>} - the canvas element created from the given HTML element
 */
const convertToCanvas = elementToExport =>
  html2canvas(elementToExport, {
    onclone: (_document, element) => {
      // set properties for scrollable divs so that it streches to fit the content.
      // below code is based on current implementation of Card.jsx component
      const scrollableParentEl = element.getElementsByClassName('containers')[0];
      stretchToContent(scrollableParentEl);

      const scrollableEl = element.getElementsByClassName('parent')[0];
      stretchToContent(scrollableEl);

      // update element height if less than the scrollable content
      if (scrollableParentEl && element.offsetHeight < scrollableParentEl.scrollHeight) {
        element.style.height = `${scrollableParentEl.scrollHeight}px`;
      }

      const canvasEl = element.getElementsByTagName('canvas')[0];
      if (canvasEl) {
        if (element.offsetHeight < canvasEl.offsetHeight) {
          element.style.height = `${canvasEl.offsetHeight + 60}px`;
        }
        if (element.offsetWidth < canvasEl.offsetWidth) {
          element.style.width = `${canvasEl.offsetWidth}px`;
        }
      }
    },
  });

/**
 * @async
 * @function getBlob
 * @param {HTMLElement} element - The HTML element to convert to a blob.
 * @returns {Promise<Blob>} The resulting Blob object.
 */
const getBlob = async element =>
  new Promise((resolve, reject) => {
    convertToCanvas(element)
      .then(canvas => {
        canvas.toBlob(blob => {
          resolve(blob);
        });
      })
      .catch(() => {
        toastNotify('error', 'Error creating image');
        reject();
      });
  });

/**
* @function getElementToExport
* @param {Object} ref - A reference to an element in the DOM.
* @return {(HTMLElement|undefined)} Returns the last child of the referenced element, if it exists;
  otherwise, returns the first table element or canvas element found within the referenced element.
  If no such element is found, returns undefined.
*/
const getElementToExport = ref => {
  const elementToExport = ref.current;
  if (!elementToExport) {
    return undefined;
  }
  const [table] = ref.current.getElementsByTagName('table');
  if (table) {
    return table;
  }
  const [canvas] = ref.current.getElementsByTagName('canvas');
  if (canvas) {
    return canvas;
  }

  return elementToExport.lastChild;
};

/**
 * @async
 * @function copyImageToClipboard
 * @param {string} name - The name of the element
 * @param {Object} ref - The ref object of the element to be copied
 * @returns {Promise} Promise - Resolves if the image is successfully copied to clipboard
 */
export async function copyImageToClipboard(name, ref) {
  const elementToExport = getElementToExport(ref);
  if (!elementToExport) {
    return;
  }
  const promiseData = [new window.ClipboardItem({ 'image/png': getBlob(elementToExport) })];

  navigator.clipboard
    .write(promiseData)
    .then(() => {
      toastNotify('success', `${name} copied to clipboard`);
    })
    .catch(async () => {
      const blob = await getBlob(elementToExport);

      navigator.clipboard
        .write([new window.ClipboardItem({ 'image/png': blob })])
        .then(() => {
          toastNotify('success', `${name} copied to clipboard`);
        })
        .catch(() => {
          toastNotify('error', 'Error copying to clipboard');
        });
    });
}

/**
 * @async
 * @function exportAsImage
 * @param {string} name - The name of the element
 * @param {Object} ref - The ref object of the element to be copied
 * @returns {Promise} Promise - Resolves if the image is successfully saved
 */
export async function exportAsImage(name, ref) {
  const elementToExport = getElementToExport(ref);
  if (!elementToExport) {
    return;
  }

  try {
    const blob = await getBlob(elementToExport);
    fileDownload(blob, `${name}_${formatDate(new Date(), dateFormats.timeStamp)}.png`);
    toastNotify('success', 'Image saved');
  } catch {
    toastNotify('error', 'Error saving image');
  }
}

/**
 * @function doPPT
 * @param {string} exportElement - the id of the element to export
 * @returns {Promise<string>} - the base64 string of the canvas
 */
export function doPPT(exportElement) {
  return html2canvas(document.querySelector(`#${exportElement}`), { scale: 2, useCORS: true }).then(
    canvas => {
      const base64Canvas = canvas.toDataURL('image/jpeg').split(';base64,')[1];
      return base64Canvas;
    },
  );
}

/**
 * @async
 * @function exportToPPT
 * @param {Object} param - The object containing the data to be exported, the url to export to, and the name of the data
 * @param {Object} param.data - The data to be exported
 * @param {string} param.url - The url to export to
 * @param {string} param.dataName - The name of the data
 * @returns {Promise} Promise - Resolves if the data is successfully exported
 */
export const exportDataWithApi = async ({
  data,
  url,
  dataName,
  onSuccess,
  onFailure,
  accessToken,
}) => {
  try {
    // Check if this is the ContributionsNetwork endpoint that needs special handling
    if (url && url.toLowerCase().includes('contributionsnetwork')) {
      // First fetch the data to transform it
      const response = await api({
        accessToken,
        method: 'POST',
        url,
        data,
      });

      // Transform the network data into a flat CSV format
      let csvContent = '';
      // Handle API response wrapper structure
      const responseData = response?.data || response;
      const authors = responseData?.authors;

      if (authors && Array.isArray(authors) && authors.length > 0) {
        // Create a flat table of connections
        const rows = [];
        authors.forEach(author => {
          const authorName = author.mentionedByName || 'Unknown';
          const authorType = author.mentionedByType || 'Unknown';

          if (author.mentions && author.mentions.length > 0) {
            author.mentions.forEach(mention => {
              rows.push({
                'Source Author': authorName,
                'Source Type': authorType,
                'Mentioned Author': mention.mentionedName || 'Unknown',
                'Mentioned Type': mention.mentionedType || 'Unknown',
                'Mention Count': mention.mentionedCount || 0,
                'Social Platform': mention.source || 'Unknown',
              });
            });
          } else {
            // Include authors with no mentions
            rows.push({
              'Source Author': authorName,
              'Source Type': authorType,
              'Mentioned Author': '',
              'Mentioned Type': '',
              'Mention Count': 0,
              'Social Platform': '',
            });
          }
        });

        // Convert to CSV
        if (rows.length > 0) {
          const headers = Object.keys(rows[0]);
          csvContent = [
            headers.join(','),
            ...rows.map(row =>
              headers
                .map(header => {
                  const value = row[header] || '';
                  const cellStr = String(value);
                  if (cellStr.includes(',') || cellStr.includes('"') || cellStr.includes('\n')) {
                    return `"${cellStr.replace(/"/g, '""')}"`;
                  }
                  return cellStr;
                })
                .join(','),
            ),
          ].join('\n');
        }
      }

      if (csvContent) {
        const name = dataName || 'Connections_Network';
        downloadData(csvContent, name);
        if (onSuccess) onSuccess();
      } else {
        throw new Error('No connections data available for export');
      }
      return;
    }

    // Default behavior for other endpoints
    const response = await api({
      accessToken,
      method: 'POST',
      url,
      exportRequest: true,
      headers: { Accept: 'text/csv' },
      data,
    });
    const name = dataName || 'Data_Export';
    downloadData(response, name);
    if (onSuccess) onSuccess();
  } catch (error) {
    toastNotify('error', 'Failed to download ', dataName);
    if (onFailure) onFailure(error);
  }
};

// Utility: fetch image and return base64 string
export function getImageBase64(url) {
  return fetch(url)
    .then(res => res.blob())
    .then(
      blob =>
        new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onloadend = () => {
            const { result } = reader;
            // Validate the base64 string
            if (result && typeof result === 'string') {
              // Check if it's a valid data URI
              if (!result.startsWith('data:')) {
                reject(new Error('Invalid data URI'));
                return;
              }

              // Ensure proper base64 padding
              const base64Part = result.split(',')[1];
              if (base64Part) {
                // Base64 strings should be divisible by 4 in length
                const paddingNeeded = (4 - (base64Part.length % 4)) % 4;
                const paddedBase64 = base64Part + '='.repeat(paddingNeeded);
                const dataUriPrefix = result.split(',')[0];
                const fixedResult = `${dataUriPrefix},${paddedBase64}`;
                resolve(fixedResult);
              } else {
                resolve(result);
              }
            } else {
              reject(new Error('Failed to read image as data URL'));
            }
          };
          reader.onerror = reject;
          reader.readAsDataURL(blob);
        }),
    );
}
